import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";

import {
  AlertFragment,
  AlertQuery,
  AlertsUpdate,
  UserPushSubscriptions,
  UserPushSubscriptionsUpsert,
  type TAlert,
} from "./queries";
import type { JSONValue } from "../../__generated__/gql/graphql";
import { createEntityQueryHook } from "../../utils/query";
import { useGraphQLClient } from "@package/graphql/react";
import { graphql, readFragment } from "../../graphql";
import { useUserId } from "../../context/auth/hooks";

const useAlertsCache = createEntityQueryHook<TAlert[]>(() => ["alerts", useUserId()]);

const useAlertsQueryOptions = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  const cache = useAlertsCache();
  return queryOptions({
    queryKey: cache.queryKey,
    queryFn: () =>
      graphql
        .execute(AlertQuery, { userId })
        .then((x) => (readFragment(AlertFragment, x.data?.alerts) ?? []) as TAlert[]),
  });
};

export const useQueryAlerts = () => useQuery(useAlertsQueryOptions());

export const useDeleteAlerts = () => {
  const cache = useAlertsCache();
  const graphql = useGraphQLClient();
  return useMutation({
    mutationFn: (ids: string[]) => graphql.execute(DeleteAlerts, { ids }),
    onMutate: async (ids) => cache.set((old) => old?.filter((x) => !ids.includes(x.id))),
    onSettled: () => cache.invalidate(),
  });
};

const DeleteAlerts = graphql(`
    mutation DeleteAlerts($ids: [String!]!) {
        delete_alerts(where: { id: { _in: $ids }}) {
            affected_rows
        }
    }
`);

export const useUpdateAlert = () => {
  const graphql = useGraphQLClient();
  const cache = useAlertsCache();
  return useMutation({
    mutationFn: ({ id, ...values }: Partial<TAlert> & { id: string }) => graphql.execute(AlertsUpdate, { id, values }),
    onMutate: ({ id, ...values }) => {
      cache.set((old) => old?.map((x) => (x.id === id ? { ...x, ...values } : x)));
    },
    onSettled: () => cache.invalidate(),
  });
};

export const useUpsertAlerts = () => {
  const graphql = useGraphQLClient();
  const cache = useAlertsCache();
  return useMutation({
    mutationFn: (rules: TAlert[]) => graphql.execute(UpsertAlerts, { objects: rules }),
    onMutate: (rules) => {
      cache.set((old) => {
        const newRules = rules.filter((x) => !old?.find((y) => y.id === x.id));
        return [
          ...(old ?? []).map((x) => {
            const newRule = rules.find((y) => y.id === x.id);
            if (newRule) return newRule;
            return x;
          }),
          ...newRules,
        ];
      });
    },
    onSettled: () => cache.invalidate(),
  });
};

const UpsertAlerts = graphql(`
    mutation UpsertAlerts(
      $objects: [alerts_insert_input!]!
    ) {
      insert_alerts(
        objects: $objects
        on_conflict: {
          constraint: alerts_pkey
          update_columns: [
            limit
            _id
            column_id
            desktop_notification_sent
            field_selector
            note
            page_id
            product_id
            push_notification_sent
            recurring
            row_id
            sound
            status
            triggered_at
            user_id
            value_below_limit
            created_at
            expired_at
          ]
        }
      ) {
        affected_rows
      }
    }
  `);

export const useUserPushSubscriptions = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useQuery({
    queryKey: ["userPushSubscriptions", userId],
    queryFn: () =>
      graphql.execute(UserPushSubscriptions, { userId: userId }).then((x) => {
        const subs = x.data?.push_subscriptions?.[0];
        if (!subs) return null;
        const subscriptions =
          typeof subs.subscriptions === "string" ? JSON.parse(subs.subscriptions) : subs.subscriptions;
        return {
          user_id: subs.user_id,
          subscriptions: subscriptions as PushSubscriptionJSON[],
        };
      }),
  });
};

export const useUserPushSubscriptionsUpsert = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useMutation({
    mutationFn: (subscriptions: PushSubscriptionJSON[]) =>
      graphql.execute(UserPushSubscriptionsUpsert, {
        userId: userId,
        subscriptions: subscriptions as unknown as JSONValue,
      }),
  });
};
